import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { RequiredLabel } from "@/components/catalog/RequiredLabel";
import { CatalogMobileActionBar } from "@/components/catalog/CatalogMobileActionBar";

interface JournalIdentifiers {
  issn?: string;
  eIssn?: string;
  issnL?: string;
}

interface JournalIdentifierStepProps {
  identifiers: JournalIdentifiers;
  onChange: (identifiers: JournalIdentifiers) => void;
  onContinue: () => void;
  valid: boolean;
  loading: boolean;
}

export function JournalIdentifierStep({
  identifiers,
  onChange,
  onContinue,
  valid,
  loading,
}: JournalIdentifierStepProps) {
  const button = (
    <Button
      type="button"
      onClick={onContinue}
      disabled={!valid || loading}
      className="min-h-11 w-full gap-2 lg:w-auto"
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
      {loading ? "Checking…" : "Continue"}
    </Button>
  );

  return (
    <>
      <Card className="overflow-hidden bg-white shadow-sm">
        <div className="space-y-6 p-6 sm:p-8">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Enter journal identifiers</h2>
            <p className="mt-2 text-sm text-gray-600">
              ISSN identifies the parent journal. Optional electronic and linking identifiers are
              saved with a new record.
            </p>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <RequiredLabel htmlFor="journal-issn" required>
                ISSN
              </RequiredLabel>
              <Input
                id="journal-issn"
                value={identifiers.issn ?? ""}
                onChange={(event) => onChange({ ...identifiers, issn: event.target.value })}
                placeholder="ISSN e.g. 0028-0836"
                className="h-12"
              />
            </div>
            <div className="space-y-2">
              <RequiredLabel htmlFor="journal-eIssn">eISSN</RequiredLabel>
              <Input
                id="journal-eIssn"
                value={identifiers.eIssn ?? ""}
                onChange={(event) => onChange({ ...identifiers, eIssn: event.target.value })}
                placeholder="eISSN e.g. 1476-4687"
                className="h-12"
              />
            </div>
            <div className="space-y-2">
              <RequiredLabel htmlFor="journal-issnL">ISSN-L</RequiredLabel>
              <Input
                id="journal-issnL"
                value={identifiers.issnL ?? ""}
                onChange={(event) => onChange({ ...identifiers, issnL: event.target.value })}
                placeholder="ISSN-L e.g. 0028-0836"
                className="h-12"
              />
            </div>
          </div>
          <div className="hidden justify-end lg:flex">{button}</div>
        </div>
      </Card>
      <CatalogMobileActionBar>{button}</CatalogMobileActionBar>
    </>
  );
}
